/**
 * Grid - Vista unificada de propiedades
 *
 * Combina:
 * - UI/UX del template original (hero, modal filtros, paginación)
 * - Datos de Firebase via useProperties hook
 * - GridProperty adaptado a datos reales
 * - Funciones avanzadas (favoritos, precios dinámicos, SEO)
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Select from 'react-select'
import Slider from 'rc-slider'
import '../../../node_modules/rc-slider/assets/index.css'

import Navbar from '../../components/navbar/navbar'
import UserNav from '../../components/navbar/user-nav'
import GridProperty from '../../components/grid-property'
import FooterTop from '../../components/footer-top'
import Footer from '../../components/footer'
import SEO from '../../components/SEO'
import { EmptyState, SkeletonGrid } from '../../components/loading-states'
import { generateCollectionPageSchema, generateItemListSchema } from '../../utils/schema'
import { useProperties, PropertyFilters, PropertySort, Propiedad } from '../../hooks/useProperties'
import { useUser } from '../../context/UserContext'
import placeholderProperty from '../../assets/img/placeholder-property.svg'

// Opciones de ordenamiento
const sortOptions = [
  { value: 'fecha_creacion-desc', label: 'Más recientes' },
  { value: 'fecha_creacion-asc', label: 'Más antiguos' },
  { value: 'precio-asc', label: 'Precio: Menor a Mayor' },
  { value: 'precio-desc', label: 'Precio: Mayor a Menor' },
  { value: 'recamaras-desc', label: 'Más recámaras' },
  { value: 'tamano_construccion-desc', label: 'Más grandes' }
]

export default function Grid() {
  const { t } = useTranslation()
  const { user } = useUser()
  const [searchParams, setSearchParams] = useSearchParams()

  // Estado del modal de filtros
  const [open, setOpen] = useState<boolean>(false)

  // Estado del slider de precio
  const [priceRange, setPriceRange] = useState<number[]>([0, 10000000])

  // Hook de propiedades
  const {
    propiedades,
    loading,
    error,
    pagination,
    fetchPropiedades,
    cargarMas,
    tiposPropiedad,
    ciudades,
    zonas,
    formatearPrecio
  } = useProperties()

  // Estado de filtros
  const [filters, setFilters] = useState<PropertyFilters>({
    busqueda: searchParams.get('busqueda') || '',
    tipo_operacion: (searchParams.get('operacion') as any) || 'todos',
    tipo_propiedad: searchParams.get('tipo') || '',
    ciudad: searchParams.get('ciudad') || '',
    precio_min: searchParams.get('precio_min') ? Number(searchParams.get('precio_min')) : undefined,
    precio_max: searchParams.get('precio_max') ? Number(searchParams.get('precio_max')) : undefined,
    recamaras_min: searchParams.get('recamaras') ? Number(searchParams.get('recamaras')) : undefined,
    banos_min: searchParams.get('banos') ? Number(searchParams.get('banos')) : undefined
  })

  // Estado de ordenamiento
  const [sort, setSort] = useState<PropertySort>({
    campo: 'fecha_creacion',
    direccion: 'desc'
  })

  // Estado de paginación local
  const [currentPage, setCurrentPage] = useState(1)
  const propertiesPerPage = 12

  // Cargar propiedades al montar
  useEffect(() => {
    fetchPropiedades(filters, sort, true)
  }, [])

  // Manejar búsqueda
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchPropiedades(filters, sort, true)

    // Actualizar URL params
    const params = new URLSearchParams()
    if (filters.busqueda) params.set('busqueda', filters.busqueda)
    if (filters.tipo_operacion !== 'todos') params.set('operacion', filters.tipo_operacion!)
    if (filters.tipo_propiedad) params.set('tipo', filters.tipo_propiedad)
    if (filters.ciudad) params.set('ciudad', filters.ciudad)
    setSearchParams(params)
  }, [filters, sort, fetchPropiedades, setSearchParams])

  // Manejar cambio de ordenamiento
  const handleSortChange = (option: any) => {
    if (!option) return
    const [campo, direccion] = option.value.split('-')
    const newSort: PropertySort = {
      campo: campo as PropertySort['campo'],
      direccion: direccion as PropertySort['direccion']
    }
    setSort(newSort)
    setCurrentPage(1)
    fetchPropiedades(filters, newSort, true)
  }

  // Manejar cambio de rango de precio
  const handlePriceRangeChange = (values: number | number[]) => {
    if (Array.isArray(values)) {
      setPriceRange(values)
    }
  }

  // Aplicar filtros del modal
  const handleApplyFilters = () => {
    const newFilters = {
      ...filters,
      precio_min: priceRange[0],
      precio_max: priceRange[1]
    }
    setFilters(newFilters)
    setCurrentPage(1)
    fetchPropiedades(newFilters, sort, true)
    setOpen(false)
  }

  // Resetear filtros
  const handleResetFilters = () => {
    const defaultFilters: PropertyFilters = {
      busqueda: '',
      tipo_operacion: 'todos',
      tipo_propiedad: '',
      ciudad: '',
      zona: '',
      precio_min: undefined,
      precio_max: undefined,
      recamaras_min: undefined,
      banos_min: undefined
    }
    setFilters(defaultFilters)
    setPriceRange([0, 10000000])
    setCurrentPage(1)
    fetchPropiedades(defaultFilters, sort, true)
    setSearchParams({})
  }

  // Cambiar página
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Calcular propiedades a mostrar en la página actual
  const startIndex = (currentPage - 1) * propertiesPerPage
  const endIndex = startIndex + propertiesPerPage
  const currentProperties = propiedades.slice(startIndex, endIndex)
  const totalPages = Math.ceil(propiedades.length / propertiesPerPage)

  // Convertir propiedad a formato de GridProperty
  const mapPropiedadToItem = (prop: Propiedad) => {
    // Preparar imágenes (debe ser array)
    let images: string[] = []
    if (prop.fotos && Array.isArray(prop.fotos) && prop.fotos.length > 0) {
      images = prop.fotos
    } else if (prop.imagen) {
      images = [prop.imagen]
    } else {
      images = [placeholderProperty]
    }

    // Preparar tags - Validar solo si es realmente nuevo (últimos 30 días) o destacado
    const tags: string[] = []
    if (prop.destacado) tags.push('Verified')

    // Solo mostrar "New" si la propiedad fue creada en los últimos 30 días
    const fechaCreacion = prop.fecha_creacion?.toDate ? prop.fecha_creacion.toDate() :
                          prop.fecha_creacion ? new Date(prop.fecha_creacion) : null
    const esNueva = fechaCreacion && (Date.now() - fechaCreacion.getTime()) < 30 * 24 * 60 * 60 * 1000
    if (esNueva) tags.push('New')

    return {
      id: prop.id,
      name: prop.nombre || prop.titulo || 'Propiedad sin nombre',
      image: images,
      tag: tags.length > 0 ? tags : [],
      tag2: prop.tipo_operacion === 'renta' ? 'For Rent' : 'For Sell',
      value: formatearPrecio(prop.precio_actual_usd || prop.precio || 0),
      loction: prop.zona || prop.area || prop.ciudad || (typeof prop.direccion === 'string' ? prop.direccion : prop.direccion?.zona || prop.direccion?.area || prop.direccion?.ciudad) || 'Sin ubicación',
      beds: `${prop.recamaras || 0} Rec`,
      size: `${prop.banos || prop.medios_banos || 0} Baños`,
      sqft: `${prop.tamano_construccion || prop.tamano_terreno || 0} m²`,
      type: prop.tipo_propiedad || prop.tipo || 'Propiedad'
    }
  }

  // Schema.org para SEO
  const collectionSchema = generateCollectionPageSchema(
    'Propiedades en Venta y Renta',
    'Explora propiedades exclusivas en Riviera Nayarit con asesores certificados por AMPI',
    '/grid'
  )

  const itemListSchema = generateItemListSchema(
    propiedades.slice(0, 10).map(item => ({
      id: item.id,
      name: item.nombre || item.titulo || 'Propiedad',
      url: `/single-property-1/${item.id}`,
      image: item.imagen || (item.fotos && item.fotos[0]) || ''
    })),
    'Propiedades en Grid',
    'Propiedades destacadas en Riviera Nayarit'
  )

  // Generar números de página
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(totalPages)
      }
    }
    return pages
  }

  return (
    <>
      <SEO
        title="Propiedades en Venta y Renta | AMPI Riviera Nayarit"
        description="Explora propiedades exclusivas en Riviera Nayarit. Encuentra casas, departamentos, terrenos y más con asesores certificados por AMPI."
        path="/grid"
        schema={[collectionSchema, itemListSchema]}
      />

      {/* Navegación */}
      {user ? (
        <UserNav nombreUsuario={user.nombre} nombreInmobiliaria={user.inmobiliaria_nombre} />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Hero Section con búsqueda */}
      <section className="bg-primary position-relative">
        <div className="position-absolute start-0 top-0 w-25 h-15 bg-light rounded-end-pill opacity-25 mt-4"></div>
        <div className="position-absolute start-0 bottom-0 w-15 h-20 bg-light rounded-top-pill opacity-25 ms-4"></div>
        <div className="position-absolute end-0 top-0 w-15 h-25 bg-light rounded-bottom-pill opacity-25 me-4"></div>
        <div className="position-absolute end-0 bottom-0 w-25 h-15 bg-light rounded-start-pill opacity-25 mb-4"></div>
        <div className="ht-30"></div>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9 col-md-12">
              <form onSubmit={handleSearch}>
                <div className="full-search-2 eclip-search italian-search hero-search-radius shadow-hard">
                  <div className="hero-search-content">
                    <div className="row align-items-center">
                      <div className="col-lg-6 col-md-6 col-sm-12">
                        <div className="form-group mb-0">
                          <div className="position-relative">
                            <input
                              type="text"
                              className="form-control border-0 ps-5"
                              placeholder={t('property.searchPlaceholder') || 'Buscar por ubicación...'}
                              value={filters.busqueda}
                              onChange={(e) => setFilters(prev => ({ ...prev, busqueda: e.target.value }))}
                            />
                            <div className="position-absolute top-50 start-0 translate-middle-y ms-2">
                              <span className="svg-icon text-primary svg-icon-2hx">
                                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M18.0624 15.3453L13.1624 20.7453C12.5624 21.4453 11.5624 21.4453 10.9624 20.7453L6.06242 15.3453C4.56242 13.6453 3.76242 11.4453 4.06242 8.94534C4.56242 5.34534 7.46242 2.44534 11.0624 2.04534C15.8624 1.54534 19.9624 5.24534 19.9624 9.94534C20.0624 12.0453 19.2624 13.9453 18.0624 15.3453Z" fill="currentColor"/>
                                  <path d="M12.0624 13.0453C13.7193 13.0453 15.0624 11.7022 15.0624 10.0453C15.0624 8.38849 13.7193 7.04535 12.0624 7.04535C10.4056 7.04535 9.06241 8.38849 9.06241 10.0453C9.06241 11.7022 10.4056 13.0453 12.0624 13.0453Z" fill="currentColor"/>
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-3 col-sm-12">
                        <button type="submit" className="btn btn-dark full-width">
                          <i className="fa-solid fa-search me-1"></i> Buscar
                        </button>
                      </div>
                      <div className="col-lg-3 col-md-3 col-sm-12">
                        <button type="button" className="btn btn-outline-light full-width" onClick={() => setOpen(true)}>
                          <i className="fa-solid fa-sliders me-1"></i> Filtros
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="ht-30"></div>
      </section>

      {/* Sección de propiedades */}
      <section className="gray-simple">
        <div className="container">
          {/* Barra de resultados */}
          <div className="row justify-content-center">
            <div className="col-lg-12 col-md-12">
              <div className="item-shorting-box">
                <div className="item-shorting clearfix">
                  <div className="left-column pull-left">
                    <h4 className="m-0 fs-6">
                      {loading ? (
                        'Cargando propiedades...'
                      ) : (
                        <>
                          Mostrando {startIndex + 1}-{Math.min(endIndex, propiedades.length)} de {propiedades.length} resultados
                        </>
                      )}
                    </h4>
                  </div>
                </div>
                <div className="item-shorting-box-right">
                  <div className="shorting-by" style={{ minWidth: '200px' }}>
                    <Select
                      options={sortOptions}
                      value={sortOptions.find(o => o.value === `${sort.campo}-${sort.direccion}`)}
                      onChange={handleSortChange}
                      placeholder="Ordenar por"
                      classNamePrefix="select"
                    />
                  </div>
                  <ul className="shorting-list">
                    <li>
                      <Link to="/grid" className="active">
                        <span className="svg-icon text-primary svg-icon-2hx">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="2" width="9" height="9" rx="2" fill="currentColor"/>
                            <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="currentColor"/>
                            <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="currentColor"/>
                            <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="currentColor"/>
                          </svg>
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/classical-layout-with-sidebar">
                        <span className="svg-icon text-muted-2 svg-icon-2hx">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M14 10V20C14 20.6 13.6 21 13 21H10C9.4 21 9 20.6 9 20V10C9 9.4 9.4 9 10 9H13C13.6 9 14 9.4 14 10ZM20 9H17C16.4 9 16 9.4 16 10V20C16 20.6 16.4 21 17 21H20C20.6 21 21 20.6 21 20V10C21 9.4 20.6 9 20 9Z" fill="currentColor"/>
                            <path d="M7 10V20C7 20.6 6.6 21 6 21H3C2.4 21 2 20.6 2 20V10C2 9.4 2.4 9 3 9H6C6.6 9 7 9.4 7 10ZM21 6V3C21 2.4 20.6 2 20 2H3C2.4 2 2 2.4 2 3V6C2 6.6 2.4 7 3 7H20C20.6 7 21 6.6 21 6Z" fill="currentColor"/>
                          </svg>
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/half-map">
                        <span className="svg-icon text-muted-2 svg-icon-2hx">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M18.0624 15.3453L13.1624 20.7453C12.5624 21.4453 11.5624 21.4453 10.9624 20.7453L6.06242 15.3453C4.56242 13.6453 3.76242 11.4453 4.06242 8.94534C4.56242 5.34534 7.46242 2.44534 11.0624 2.04534C15.8624 1.54534 19.9624 5.24534 19.9624 9.94534C20.0624 12.0453 19.2624 13.9453 18.0624 15.3453Z" fill="currentColor"/>
                            <path d="M12.0624 13.0453C13.7193 13.0453 15.0624 11.7022 15.0624 10.0453C15.0624 8.38849 13.7193 7.04535 12.0624 7.04535C10.4056 7.04535 9.06241 8.38849 9.06241 10.0453C9.06241 11.7022 10.4056 13.0453 12.0624 13.0453Z" fill="currentColor"/>
                          </svg>
                        </span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Grid de propiedades */}
          {loading && propiedades.length === 0 ? (
            <SkeletonGrid count={12} columns={3} variant="property" />
          ) : error ? (
            <div className="row">
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  {error}
                  <button className="btn btn-link" onClick={() => fetchPropiedades(filters, sort, true)}>
                    Reintentar
                  </button>
                </div>
              </div>
            </div>
          ) : propiedades.length === 0 ? (
            <EmptyState
              icon="fa-building"
              title={t('property.noProperties') || 'No se encontraron propiedades'}
              message={t('property.noMatch') || 'Intenta ajustar los filtros'}
              actionText="Limpiar filtros"
              onAction={handleResetFilters}
            />
          ) : (
            <>
              <div className="row justify-content-center g-4">
                {currentProperties.map((prop, index) => (
                  <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12" key={`${prop.id}-${index}`}>
                    <GridProperty item={mapPropiedadToItem(prop)} border={false} />
                  </div>
                ))}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="row mt-5">
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <ul className="pagination p-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="fa-solid fa-arrow-left-long"></i>
                        </button>
                      </li>

                      {getPageNumbers().map((page, index) => (
                        <li
                          key={index}
                          className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                        >
                          {page === '...' ? (
                            <span className="page-link">...</span>
                          ) : (
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page as number)}
                            >
                              {page}
                            </button>
                          )}
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="fa-solid fa-arrow-right-long"></i>
                        </button>
                      </li>
                    </ul>

                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Página {currentPage} de {totalPages} ({propiedades.length} propiedades totales)
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      {/* Modal de Filtros Avanzados */}
      {open && (
        <div className="modal fade bd-example-modal-lg show d-block" style={{ backgroundColor: '#0000008a' }}>
          <div className="modal-dialog modal-lg filter_scroll" role="document">
            <div className="modal-content">
              <span className="mod-close" onClick={() => setOpen(false)} style={{ cursor: 'pointer' }}>
                <i className="fa-solid fa-xmark"></i>
              </span>
              <div className="modal-body">
                <div className="filter_modal">
                  <div className="filter_modal_inner">
                    <div className="filter_modal_flex">

                      <div className="adv_ft_title">
                        <h5>{t('filters.advancedFilters') || 'Filtros Avanzados'}</h5>
                      </div>

                      {/* Rango de precio */}
                      <div className="flt_single_item">
                        <div className="flt_item_lablel">
                          <label>{t('filters.price') || 'Precio'} (USD)</label>
                        </div>
                        <div className="flt_item_content flcl">
                          <div className="rg-slider">
                            <Slider
                              range
                              min={0}
                              max={10000000}
                              step={50000}
                              value={priceRange}
                              onChange={handlePriceRangeChange}
                              allowCross={false}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                              <span className="badge bg-secondary">{formatearPrecio(priceRange[0])}</span>
                              <span className="badge bg-secondary">{formatearPrecio(priceRange[1])}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Tipo de propiedad */}
                      <div className="flt_single_item">
                        <div className="flt_item_lablel">
                          <label>{t('filters.propertyType') || 'Tipo de Propiedad'}</label>
                        </div>
                        <div className="flt_item_content">
                          {tiposPropiedad.map(tipo => (
                            <div className="switchbtn-wrap" key={tipo}>
                              <div className="switchbtn">
                                <input
                                  id={`tipo-${tipo}`}
                                  className="switchbtn-checkbox"
                                  type="checkbox"
                                  checked={filters.tipo_propiedad === tipo}
                                  onChange={() => setFilters(prev => ({
                                    ...prev,
                                    tipo_propiedad: prev.tipo_propiedad === tipo ? '' : tipo
                                  }))}
                                />
                                <label className="switchbtn-label" htmlFor={`tipo-${tipo}`}>{tipo}</label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Zona */}
                      <div className="flt_single_item">
                        <div className="flt_item_lablel">
                          <label>Zona</label>
                        </div>
                        <div className="flt_item_content">
                          {zonas.map(zona => (
                            <div className="switchbtn-wrap" key={zona}>
                              <div className="switchbtn">
                                <input
                                  id={`zona-${zona}`}
                                  className="switchbtn-checkbox"
                                  type="checkbox"
                                  checked={filters.zona === zona}
                                  onChange={() => setFilters(prev => ({
                                    ...prev,
                                    zona: prev.zona === zona ? '' : zona
                                  }))}
                                />
                                <label className="switchbtn-label" htmlFor={`zona-${zona}`}>{zona}</label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recámaras */}
                      <div className="flt_single_item">
                        <div className="flt_item_lablel">
                          <label>{t('propertyFeatures.bedrooms') || 'Recámaras'}</label>
                        </div>
                        <div className="flt_item_content">
                          {[1, 2, 3, 4, 5].map(num => (
                            <div className="switchbtn-wrap" key={num}>
                              <div className="switchbtn">
                                <input
                                  id={`rec-${num}`}
                                  className="switchbtn-checkbox"
                                  type="checkbox"
                                  checked={filters.recamaras_min === num}
                                  onChange={() => setFilters(prev => ({
                                    ...prev,
                                    recamaras_min: prev.recamaras_min === num ? undefined : num
                                  }))}
                                />
                                <label className="switchbtn-label" htmlFor={`rec-${num}`}>
                                  {num === 5 ? '5+' : num}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Baños */}
                      <div className="flt_single_item">
                        <div className="flt_item_lablel">
                          <label>{t('propertyFeatures.bathrooms') || 'Baños'}</label>
                        </div>
                        <div className="flt_item_content">
                          {[1, 2, 3, 4].map(num => (
                            <div className="switchbtn-wrap" key={num}>
                              <div className="switchbtn">
                                <input
                                  id={`ban-${num}`}
                                  className="switchbtn-checkbox"
                                  type="checkbox"
                                  checked={filters.banos_min === num}
                                  onChange={() => setFilters(prev => ({
                                    ...prev,
                                    banos_min: prev.banos_min === num ? undefined : num
                                  }))}
                                />
                                <label className="switchbtn-label" htmlFor={`ban-${num}`}>
                                  {num === 4 ? '4+' : num}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Ciudad */}
                      {ciudades.length > 0 && (
                        <div className="flt_single_item">
                          <div className="flt_item_lablel">
                            <label>{t('filters.city') || 'Ciudad'}</label>
                          </div>
                          <div className="flt_item_content">
                            {ciudades.slice(0, 8).map(ciudad => (
                              <div className="switchbtn-wrap" key={ciudad}>
                                <div className="switchbtn">
                                  <input
                                    id={`ciudad-${ciudad}`}
                                    className="switchbtn-checkbox"
                                    type="checkbox"
                                    checked={filters.ciudad === ciudad}
                                    onChange={() => setFilters(prev => ({
                                      ...prev,
                                      ciudad: prev.ciudad === ciudad ? '' : ciudad
                                    }))}
                                  />
                                  <label className="switchbtn-label" htmlFor={`ciudad-${ciudad}`}>{ciudad}</label>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Botones de acción */}
                      <div className="flt_single_item">
                        <div className="d-flex gap-2 justify-content-end mt-4">
                          <button className="btn btn-outline-secondary" onClick={handleResetFilters}>
                            <i className="fa-solid fa-rotate-left me-1"></i> Resetear
                          </button>
                          <button className="btn btn-primary" onClick={handleApplyFilters}>
                            <i className="fa-solid fa-check me-1"></i> Aplicar Filtros
                          </button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
